// Moment
import moment, { Moment } from 'moment';

// Reactive X
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';

// Internal dependencies
import { Device } from '../../../providers/traccar-client';
import { Tracker } from '../../../providers/tracker-factory.service';

import { TraccarUser as User } from '../user/traccar-user';


export class Bike {

  /* ATTRIBUTES */

  public readonly isGuarded$: Observable<boolean> = this.tracker.deviceChanges$.pipe(
    map((device: Device) => device.attributes?.guarded ?? false),
    startWith(false),
    distinctUntilChanged(),
  );

  public readonly hasAlarm$: Observable<boolean> = this.tracker.deviceChanges$.pipe(
    map((device: Device) => device.attributes?.alarm ?? false),
    startWith(false),
    distinctUntilChanged(),
  );

  public readonly isStolen$: Observable<boolean> = combineLatest([
    this.isGuarded$,
    this.hasAlarm$,
  ]).pipe(
    map(([isGuarded, hasAlarm]) => isGuarded && hasAlarm),
    distinctUntilChanged(),
  );


  /* LIFECYCLE */

  public constructor(public tracker: Tracker) {}

  /* METHODS */

  public delete(): Observable<void> {
    return this.tracker.delete();
  }

  public rename(newName: string): Observable<void> {
    return this.tracker.setName(newName).pipe(
      map(() => undefined),
    );
  }

  public isOwner(user: User): boolean {
    // Workaround for 'pre-sharing' B2B trackers without an 'ownerId' -> no owner (see PB-1616)
    if (!this.device.attributes.ownerId && this.device.attributes?.B2B && user.attributes?.category !== 'B2B') {
      return false;
    }

    return !this.device.attributes.ownerId || this.device.attributes.ownerId.toString() === user.id.toString();
  }

  public isTrackerReplacementAvailable(user: User): boolean {
    const deletionLockedUntil = this.device.attributes?.deletionLockedUntil;

    return (
      !this.uniqueId?.includes('deleted-') &&
      !!deletionLockedUntil &&
      this.isOwner(user) &&
      moment().isBefore(deletionLockedUntil)
    );
  }

  public async toggleGuarding(): Promise<void> {
    return this.tracker.toggleGuarded().toPromise();
  }

  /* ACCESSORS */

  public get isB2B(): boolean {
    return this.device.attributes.B2B === true;
  }

  public get isDisabled(): boolean {
    return this.device.disabled ?? false;
  }

  public get isGuarded(): boolean {
    return this.device.attributes?.guarded ?? false;
  }

  public get isInactive(): boolean {
    return (this.device.attributes.gpsDisabled ?? false) || !this.hasOnboardingFinished;
  }

  public get isStolen(): boolean {
    return this.isGuarded && this.hasAlarm;
  }

  public get hasAlarm(): boolean {
    return this.device.attributes?.alarm ?? false;
  }

  public get id(): number | undefined {
    return this.device.id ?? undefined;
  }

  public get uniqueId(): string | undefined {
    return this.device.uniqueId ?? undefined;
  }

  public get name(): string | undefined {
    return this.device.name ?? undefined;
  }

  public get image(): string | undefined {
    const bikePictures = this.device.attributes?.passport?.bikePictures;
    return bikePictures?.length > 0 ? bikePictures[0] : undefined;
  }

  public get isBikeTrax(): boolean {
    return !this.uniqueId?.includes('app');
  }

  public get battery(): number | undefined {
    return this.tracker.batteryLevel;
  }

  public get estimatedStandByTime(): number | undefined {
    return Math.trunc((this.battery / 100) * (this.tracker.is4G ? 180 : 21) * this.batteryMultiplier);
  }

  public get estimatedBatteryDuration(): number | undefined {
    return Math.trunc((this.battery / 100) * 14 * this.batteryMultiplier);
  }

  public get batteryMultiplier(): number {
    return this.device.attributes.doubleBattery ? 2 : 1;
  }

  public get isCharging(): boolean | undefined {
    return this.tracker.isCharging;
  }

  public get lastPosition(): number | undefined {
    return this.device.positionId ?? undefined;
  }

  public get lastUpdated(): Moment | undefined {
    return this.device.lastUpdate ? moment(this.device.lastUpdate) : undefined;
  }

  public get device(): Device {
    return this.tracker.latestDevice;
  }

  public get hasOnboardingFinished(): boolean {
    return !this.isBikeTrax || (this.device.attributes?.onboardingFinished ?? true);
  }

  // There are two cases for which we want to disable guarding functionality:
  // 1. tracker device has not yet send a position to the backend -> no guard fence can be created.
  // 2. onboarding process did not finish
  // For combineLatest to provide values lastPositionSource also needs to provide a value. As this is enough to tell
  // that the backend received a position for this device, we do not need to check for the position here
  public get canActivateGuardMode(): boolean {
    return this.lastPosition !== undefined && this.hasOnboardingFinished;
  }
}
